import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Crown, Gift, Sparkles, Lock, Star, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MembershipPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/membership/signup`, formData);
      toast.success('Welcome to The Inner Circle! We will contact you shortly.');
      setFormData({ name: '', email: '', phone: '' });
    } catch (error) {
      if (error.response?.data?.detail === 'Email already registered') {
        toast.error('This email is already a member');
      } else {
        toast.error('Failed to submit. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    { icon: Crown, title: 'Early Access', description: 'Be the first to discover new collections' },
    { icon: Gift, title: 'Exclusive Gifts', description: 'Receive complimentary samples and surprises' },
    { icon: Sparkles, title: 'Private Events', description: 'Invitations to exclusive launches' },
    { icon: Lock, title: 'Reserved Collections', description: 'Access to members-only fragrances' },
    { icon: Star, title: 'Personal Concierge', description: 'Dedicated fragrance consultation' },
    { icon: Check, title: 'Free Shipping', description: 'Complimentary delivery worldwide' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] pt-24" data-testid="membership-page">
      {/* Hero */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 arabian-pattern opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#050505]/50 to-[#050505]" />
        
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <p className="font-arabic text-3xl text-[#D4AF37]/60 mb-4">النادي الحصري</p>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl tracking-wide text-white mb-6">
            THE INNER <span className="text-[#D4AF37]">CIRCLE</span>
          </h1>
          <p className="font-body text-white/60 text-lg max-w-2xl mx-auto">
            Join an exclusive community of fragrance connoisseurs and experience the world of Qasr Parfumerie like never before.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="font-heading text-xs tracking-[0.3em] text-[#D4AF37] mb-4">EXCLUSIVE BENEFITS</p>
            <h2 className="font-heading text-4xl tracking-wide text-white">
              MEMBER <span className="text-[#D4AF37]">PRIVILEGES</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit) => (
              <div
                key={benefit.title}
                className="p-8 border border-[#D4AF37]/20 bg-[#050505] hover:border-[#D4AF37]/40 transition-colors"
              >
                <div className="w-14 h-14 border border-[#D4AF37]/30 flex items-center justify-center mb-6">
                  <benefit.icon size={24} className="text-[#D4AF37]" />
                </div>
                <h3 className="font-heading text-lg tracking-wide text-white mb-2">{benefit.title}</h3>
                <p className="font-body text-white/50 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-24">
        <div className="max-w-xl mx-auto px-6">
          <div className="border border-[#D4AF37]/30 bg-[#0a0a0a] p-10">
            <div className="text-center mb-10">
              <p className="font-arabic text-xl text-[#D4AF37]/60 mb-2">انضم إلينا</p>
              <h2 className="font-heading text-3xl tracking-wide text-white mb-4">
                REQUEST <span className="text-[#D4AF37]">MEMBERSHIP</span>
              </h2>
              <p className="font-body text-white/50 text-sm">
                Membership is by invitation only. Submit your application and our team will review it.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="membership-form">
              <div>
                <label className="font-heading text-xs tracking-[0.2em] text-[#D4AF37] mb-2 block">FULL NAME</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="membership-name-input"
                  className="w-full bg-[#050505] border border-[#D4AF37]/30 focus:border-[#D4AF37] text-white px-4 py-4 outline-none font-body"
                  required
                />
              </div>

              <div>
                <label className="font-heading text-xs tracking-[0.2em] text-[#D4AF37] mb-2 block">EMAIL</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="membership-email-input"
                  className="w-full bg-[#050505] border border-[#D4AF37]/30 focus:border-[#D4AF37] text-white px-4 py-4 outline-none font-body"
                  required
                />
              </div>

              <div>
                <label className="font-heading text-xs tracking-[0.2em] text-[#D4AF37] mb-2 block">PHONE</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="membership-phone-input"
                  className="w-full bg-[#050505] border border-[#D4AF37]/30 focus:border-[#D4AF37] text-white px-4 py-4 outline-none font-body"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="membership-submit-button"
                className="w-full bg-[#D4AF37] text-[#050505] py-5 font-heading text-sm tracking-[0.2em] hover:bg-[#E5C158] transition-colors disabled:opacity-50"
              >
                {loading ? 'SUBMITTING...' : 'SUBMIT APPLICATION'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MembershipPage;
